import { useAuth } from '../../hooks/useAuth';
// import { usePermissions } from '../../hooks/usePermissions';
import NotificationBell from '../Notifications/NotificationBell';
import SearchButton from '../Search/SearchButton';
import ChimeLogo from '../Logo/ChimeLogo';
import { NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import packageJson from '../../../package.json';
import { motion } from 'framer-motion';
import { fadeInDown } from '../../utils/animations';

export default function Header() {
  const { user, isImpersonating, stopImpersonation } = useAuth();
  // const { canCreatePosts } = usePermissions();
  // const location = useLocation();
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const frontendVersion = packageJson.version;
  const [backendVersion, setBackendVersion] = useState<string>('...');
  const [impersonationData, setImpersonationData] = useState<any>(null);

  // Load impersonation data from localStorage
  useEffect(() => {
    const data = localStorage.getItem('chime_impersonation');
    console.log('🎭 Header: Checking impersonation data:', { isImpersonating, hasData: !!data });
    if (data) {
      try {
        const parsed = JSON.parse(data);
        console.log('🎭 Header: Parsed impersonation data:', parsed);
        setImpersonationData(parsed);
      } catch (e) {
        console.error('Failed to parse impersonation data:', e);
      }
    } else {
      setImpersonationData(null);
    }
  }, [isImpersonating]); // Re-run when impersonation state changes

  useEffect(() => {
    // Fetch backend version from health endpoint
    const fetchBackendVersion = async () => {
      try {
        // Use VITE_API_BASE_URL directly since /health is not proxied
        const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
        const response = await fetch(`${API_BASE_URL}/health`);
        if (response.ok) {
          const data = await response.json();
          setBackendVersion(data.version || 'unknown');
        }
      } catch (error) {
        console.warn('Failed to fetch backend version:', error);
        setBackendVersion('error');
      }
    };

    fetchBackendVersion();
  }, []);

  const handleExitImpersonation = () => {
    stopImpersonation();
    window.location.href = '/admin/users';
  };

  console.log('🎭 Header render:', { isImpersonating, hasImpersonationData: !!impersonationData, impersonationData });

  return (
    <>
      {impersonationData && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg border-b-4 border-orange-700">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center space-x-4">
                <div className="bg-white bg-opacity-20 p-2 rounded-full">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold opacity-90">🎭 Impersonation Mode Active</div>
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="bg-white bg-opacity-20 px-3 py-1 rounded-md">
                      <div className="text-xs opacity-75">Admin:</div>
                      <div className="font-semibold">{impersonationData.originalAdminUser?.name || impersonationData.originalAdminUser?.email}</div>
                    </div>
                    <svg className="w-5 h-5 opacity-75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    <div className="bg-white bg-opacity-30 px-3 py-1 rounded-md">
                      <div className="text-xs opacity-75">Viewing as:</div>
                      <div className="font-bold">{user?.name || user?.email}</div>
                      <div className="text-xs">{user?.userType === 'surgeon' ? '👨‍⚕️ Surgeon' : user?.userType === 'staff' ? '👔 Staff' : '👤 User'}</div>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={handleExitImpersonation}
                className="px-4 py-2 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-colors shadow-md"
              >
                Return to Admin
              </button>
            </div>
          </div>
        </div>
      )}
      <motion.header
        className="sticky top-0 z-40 backdrop-blur-md bg-gradient-to-r from-white via-green-50/30 to-emerald-50/30 dark:from-neutral-900 dark:via-green-950/20 dark:to-emerald-950/20 border-b border-green-100/50 dark:border-green-900/30"
        style={{
          boxShadow: '0 4px 20px rgba(57, 181, 74, 0.08), 0 2px 8px rgba(141, 198, 63, 0.06)'
        }}
        variants={fadeInDown}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0 flex items-center space-x-3">
              <NavLink to="/" className="flex items-center px-3 py-2 hover:opacity-80 transition-opacity cursor-pointer">
                <ChimeLogo width={120} height={40} />
              </NavLink>
              {environment === 'development' && (
                <span className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs font-medium px-2 py-1 rounded-full">
                  DEV
                </span>
              )}
              {environment === 'staging' && (
                <span className="bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 text-xs font-medium px-2 py-1 rounded-full">
                  STAGING
                </span>
              )}
              {environment !== 'production' && (
                <div className="flex items-center space-x-1">
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-mono px-2 py-1 rounded">
                    FE v{frontendVersion}
                  </span>
                  <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-mono px-2 py-1 rounded">
                    BE v{backendVersion}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <SearchButton />
            <NotificationBell />
          </div>
        </div>
      </div>
    </motion.header>
    </>
  );
}